import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Pencil,
  Trash2,
  KeyRound,
  Filter,
  X,
  Download,
  Search,
} from "lucide-react";
import { Button } from "./ui/button";
import DataTable from "./ui/DataTable";
import Pagination from "./ui/Pagination";
import { userService } from "../services/userService";
import { clientService } from "../services/clientService";
import ConfirmDialog from "./ui/ConfirmDialog";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  formatName,
  formatRole,
  formatEmail,
  formatDateTime,
} from "../utils/formatters";
import * as XLSX from "xlsx";

const ManageUserTable = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pageSize] = useState(10);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [tempClientId, setTempClientId] = useState(""); // Pending selection, applied only on Apply Filter
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Change password dialog state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Load clients for filter dropdown
  useEffect(() => {
    loadClients();
  }, []);

  // Prevent background scroll when filter sidebar is open
  useEffect(() => {
    if (showFilterSidebar) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [showFilterSidebar]);

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const dropdownResponse = await clientService.getClientsDropdown();
      if (dropdownResponse.success && dropdownResponse.data?.clients) {
        setClients(dropdownResponse.data.clients);
      } else {
        // Fallback to list endpoint
        const response = await clientService.getClients({
          page: 1,
          limit: 1000,
          is_active: true,
          sort_by: "client_name",
          sort_order: "asc",
        });
        if (response.success && response.data?.clients) {
          setClients(response.data.clients);
        }
      }
    } catch (error) {
      console.error("Error loading clients:", error);
    } finally {
      setLoadingClients(false);
    }
  };

  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const loadingRef = useRef(false);
  const prevFiltersRef = useRef({ search: "", client_id: "" });

  const loadUsers = useCallback(
    async (page = 1, filters = {}) => {
      // Prevent concurrent calls
      if (loadingRef.current) {
        return;
      }

      try {
        loadingRef.current = true;
        setLoading(true);

        // Build filter object
        const filterObj = { is_deleted: false };

        // Add search filter (use applied search query, not the input value)
        if (appliedSearchQuery.trim()) {
          filterObj.search = appliedSearchQuery.trim();
        }

        // Add client filter
        if (selectedClientId) {
          filterObj.client_id = selectedClientId;
        }

        // Merge with additional filters
        Object.assign(filterObj, filters);

        const response = await userService.findUsers(filterObj);

        if (response.success && response.data?.users) {
          const allUsers = response.data.users;
          const count = response.data.count || allUsers.length;

          // Client-side pagination
          const startIndex = (page - 1) * pageSize;
          const endIndex = startIndex + pageSize;
          const paginatedUsers = allUsers.slice(startIndex, endIndex);

          setUsers(paginatedUsers);
          setTotalUsers(count);
          setTotalPages(Math.ceil(count / pageSize));
          setCurrentPage(page);
        } else {
          setUsers([]);
          setTotalUsers(0);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error loading users:", error);
        toast.error("Failed to load users");
        setUsers([]);
        setTotalUsers(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [pageSize, appliedSearchQuery, selectedClientId],
  );

  // Load users on initial mount
  useEffect(() => {
    loadUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load users when filters change - only if filters actually changed
  useEffect(() => {
    const currentFilters = {
      search: appliedSearchQuery.trim(),
      client_id: selectedClientId || "",
    };

    // Compare with previous filters
    const filtersChanged =
      prevFiltersRef.current.search !== currentFilters.search ||
      prevFiltersRef.current.client_id !== currentFilters.client_id;

    if (filtersChanged) {
      prevFiltersRef.current = currentFilters;
      loadUsers(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearchQuery, selectedClientId]);

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchQuery(searchQuery);
    // Don't call loadUsers directly - useEffect will handle it when appliedSearchQuery changes
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleApplyFilter = () => {
    setSelectedClientId(tempClientId);
    setShowFilterSidebar(false);
    // useEffect will handle loadUsers when selectedClientId changes
  };

  const handleClearFilter = () => {
    setSelectedClientId("");
    setTempClientId("");
    setSearchQuery("");
    setAppliedSearchQuery("");
    setShowFilterSidebar(false);
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);

      // Build filters - include both search and client filter
      const filters = { is_deleted: false };
      if (appliedSearchQuery.trim()) {
        filters.search = appliedSearchQuery.trim();
      }
      if (selectedClientId) {
        filters.client_id = selectedClientId;
      }

      // Fetch all users matching the filters (no pagination for export)
      const response = await userService.findUsers(filters);

      if (!response.success || !response.data?.users) {
        toast.error("Failed to fetch users for export");
        return;
      }

      const users = response.data.users;

      // Format data for Excel
      const excelData = users.map((user, index) => ({
        "S.No": index + 1,
        Name: formatName(user.full_name) || "N/A",
        Email: formatEmail(user.email) || "N/A",
        "Client Name": user.client_name || "N/A",
        Role: formatRole(user.role) || "N/A",
        Department: user.department ? formatName(user.department) : "N/A",
        "Created By": formatName(user.created_by) || "N/A",
        "Updated By": formatName(user.updated_by) || "N/A",
        "Created At": user.created_at ? formatDateTime(user.created_at) : "N/A",
        "Updated At": user.updated_at ? formatDateTime(user.updated_at) : "N/A",
        Status: user.is_active ? "Active" : "Inactive",
      }));

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

      // Generate filename with timestamp
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);
      const filename = `users_export_${timestamp}.xlsx`;

      // Write file and trigger download
      XLSX.writeFile(workbook, filename);

      toast.success(`Exported ${users.length} users to ${filename}`);
    } catch (error) {
      console.error("Error exporting users:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to export users";
      toast.error(errorMessage);
    } finally {
      setExportLoading(false);
    }
  };

  const handleEditUser = (user) => {
    navigate(`/users/edit/${user.user_id}?tab=manage`);
  };

  const handleDeleteUser = (user) => {
    setDeletingUser(user);
    setShowDeleteDialog(true);
  };

  const handleChangePassword = (user) => {
    setPasswordUser(user);
    setNewPassword("");
    setShowPasswordDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (!deletingUser) return;

    try {
      setDeleteLoading(true);
      await userService.updateUser(deletingUser.user_id, { is_deleted: true });
      toast.success("User deleted successfully");
      await loadUsers(currentPage);
      setShowDeleteDialog(false);
      setDeletingUser(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete user";
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmChangePassword = async () => {
    if (!passwordUser || !newPassword.trim()) {
      toast.error("Please enter a new password");
      return;
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      toast.error(
        "Password must be at least 8 characters with uppercase, lowercase, and number",
      );
      return;
    }

    try {
      setPasswordLoading(true);
      await userService.changeUserPassword(passwordUser.user_id, newPassword);
      toast.success("Password changed successfully");
      setShowPasswordDialog(false);
      setPasswordUser(null);
      setNewPassword("");
    } catch (error) {
      console.error("Error changing password:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to change password";
      toast.error(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const columns = [
    {
      key: "full_name",
      header: "Name",
      render: (value) => formatName(value) || "-",
    },
    {
      key: "email",
      header: "Email",
      render: (value) => formatEmail(value) || "-",
    },
    {
      key: "client_name",
      header: "Client Name",
      render: (value) => value || "N/A",
    },
    {
      key: "updated_by",
      header: "Updated By",
      render: (value) => formatName(value) || "-",
    },
    {
      key: "created_by",
      header: "Created By",
      render: (value) => formatName(value) || "-",
    },
    {
      key: "actions",
      header: "Actions",
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleEditUser(row)}
            title="Edit user"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleChangePassword(row)}
            title="Change password"
          >
            <KeyRound className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDeleteUser(row)}
            title="Delete user"
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 relative">
      {/* Search and Action Bar */}
      <div className="flex items-center justify-between gap-4">
        {/* Search Box */}
        <div className="flex-1 max-w-md">
          <div className="relative flex items-center">
            <Search className="absolute left-3 w-5 h-5 text-text-tertiary" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
              className="w-full pl-10 pr-24 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder:text-text-tertiary"
            />
            <Button
              onClick={handleSearchSubmit}
              size="sm"
              className="absolute right-2"
            >
              Search
            </Button>
          </div>
        </div>

        {/* Filter and Export Buttons */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setTempClientId(selectedClientId);
              setShowFilterSidebar(true);
            }}
            className="relative"
          >
            <Filter className="w-4 h-4 mr-2" />
            <span>Filter</span>
            {/* Filter count badge */}
            {(selectedClientId || appliedSearchQuery.trim()) && (
              <span className="absolute -top-2 -right-2 bg-primary-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {(selectedClientId ? 1 : 0) +
                  (appliedSearchQuery.trim() ? 1 : 0)}
              </span>
            )}
          </Button>
          <Button
            onClick={handleExport}
            disabled={exportLoading}
            className="disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4 mr-2" />
            {exportLoading ? "Exporting..." : "Export"}
          </Button>
        </div>
      </div>

      {/* Users Table */}
      <div className=" overflow-hidden table-wrapper">
        <DataTable
          data={users}
          columns={columns}
          loading={loading}
          emptyMessage="No users found."
        />

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalUsers}
          itemsPerPage={pageSize}
          onPageChange={loadUsers}
        />
      </div>

      {/* Filter Sidebar */}
      {showFilterSidebar && (
        <div
          className="fixed left-0 right-0 bottom-0 z-30 overflow-hidden"
          style={{ top: "2rem" }}
        >
          {/* Backdrop - semi-transparent overlay (use /30 for Tailwind v3 opacity) */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowFilterSidebar(false)}
          />

          {/* Sidebar - starts below top nav (64px = h-16) */}
          <div className="absolute right-0 top-0 bottom-0 w-[17.5rem] bg-bg-primary shadow-xl z-40">
            <div className="flex flex-col h-full ">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border-primary relative z-50">
                <h2 className="text-xl font-semibold text-text-primary">
                  Filters
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowFilterSidebar(false);
                  }}
                  className="relative z-50"
                  aria-label="Close filters"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Filter Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Client Filter */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Client Name
                  </label>
                  <select
                    value={tempClientId}
                    onChange={(e) => setTempClientId(e.target.value)}
                    className="w-full px-3 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 placeholder:text-text-tertiary"
                    disabled={loadingClients}
                  >
                    <option value="">All Clients</option>
                    <option value="__NA__">
                      N/A (Chief Admin / No Client)
                    </option>
                    {clients.map((client) => (
                      <option key={client.client_id} value={client.client_id}>
                        {client.client_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="border-t border-border-primary p-4 space-y-2">
                <Button onClick={handleApplyFilter} className="w-full">
                  Apply Filter
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClearFilter}
                  className="w-full"
                >
                  Clear Filter
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDeletingUser(null);
        }}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete "${formatName(deletingUser?.full_name) || formatEmail(deletingUser?.email)}"? This action cannot be undone.`}
        confirmText="Delete User"
        confirmVariant="danger"
        loading={deleteLoading}
      />

      {/* Change Password Dialog */}
      <ConfirmDialog
        isOpen={showPasswordDialog}
        onClose={() => {
          setShowPasswordDialog(false);
          setPasswordUser(null);
          setNewPassword("");
        }}
        onConfirm={confirmChangePassword}
        title="Change Password"
        message={
          <div className="space-y-4">
            <p>
              Enter new password for{" "}
              {formatName(passwordUser?.full_name) ||
                formatEmail(passwordUser?.email)}
              :
            </p>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              autoComplete="new-password"
              className="w-full px-3 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 placeholder:text-text-tertiary"
            />
          </div>
        }
        confirmText="Change Password"
        confirmVariant="primary"
        loading={passwordLoading}
      />
    </div>
  );
};

export default ManageUserTable;
